'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import CheckEnterRoomScreen from '@/app/[locale]/(worlds)/[roomId]/components/CheckEnterRoomScreen';
import Full3dMobileStickyBannerAd from '@/app/[locale]/(worlds)/[roomId]/components/Full3dMobileStickyBannerAd';
import IframeContent from '@/app/[locale]/(worlds)/[roomId]/components/IframeContent';
import IosSwipeFullscreenLayer from '@/app/[locale]/(worlds)/[roomId]/components/IosSwipeFullscreenLayer';
import TrailerCarousel from '@/app/[locale]/(worlds)/[roomId]/components/TrailerCarousel';
import TrailerThumbnails from '@/app/[locale]/(worlds)/[roomId]/components/TrailerThumbnails';
import VastAdOverlay from '@/app/[locale]/(worlds)/[roomId]/components/VastAdOverlay';
import OptimizedImage from '@/components/OptimizedImage';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { CrossPlatformGatingProvider } from '@/context/CrossPlatformGatingContext';
import { TrailerViewMediaProvider } from '@/context/TrailerViewMediaContext';
import { useUAContext } from '@/context/UAContext';
import { useEnableFull3d } from '@/hooks/useEnableFull3d';
import { useFullscreenBodyStyle } from '@/hooks/useFullscreenBodyStyle';
import { useIosSwipeFullscreen } from '@/hooks/useIosSwipeFullscreen';
import { useTransparentBackground } from '@/hooks/useTransparentBackground';
import { consumeFeedAttribution, hashAccountId, pushFeedAnalyticsEvent } from '@/lib/feed-analytics';
import { cn } from '@/lib/utils';
type WorldProps = {
  className?: string;
  setScrollLock?: (isLocked: boolean) => void;
  video?: {
    backgroundPlay?: boolean;
    containerClassName?: string;
  };
};
function World(t0) {
  const $ = _c(34);
  const {
    className,
    setScrollLock,
    video
  } = t0;
  const worldRootRef = useRef(null);
  const {
    isTrailer,
    roomData,
    shouldShowMobileTip,
    shouldShowTrailerThumbnails
  } = useCheckEnterRoomContext();
  const enableFull3d = useEnableFull3d();
  const {
    isMobile,
    isMobileLandscape
  } = useUAContext();
  const isMobilePortrait = isMobile && !isMobileLandscape;
  const isVideoWorld = roomData.scene_type === "blendvision" || !!roomData.vod_id;
  const sceneSettings = roomData.scene.settings;
  const is3DCommerceTransparentBg = Boolean(sceneSettings?.eCommerceConfigs?.is3DEC && sceneSettings.eCommerceConfigs.transparentBackground);
  const shouldTransparentPageBackground = is3DCommerceTransparentBg && enableFull3d;
  const iosSwipeFullscreen = useIosSwipeFullscreen();
  useFullscreenBodyStyle(iosSwipeFullscreen.active);
  useTransparentBackground(shouldTransparentPageBackground);
  const hasEmittedSuccessRef = useRef(false);
  const {
    accountId: currentAccountId
  } = useConfigsContext();
  let t1;
  let t2;
  if ($[0] !== currentAccountId || $[1] !== enableFull3d || $[2] !== roomData.hub_sid) {
    t1 = () => {
      if (!enableFull3d || hasEmittedSuccessRef.current) {
        return;
      }
      if (typeof window === "undefined") {
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      const attributionId = urlParams.get("__feed_attr");
      if (!attributionId) {
        return;
      }
      const hubSid = roomData.hub_sid;
      if (!consumeFeedAttribution(attributionId, hubSid)) {
        return;
      }
      hasEmittedSuccessRef.current = true;
      hashAccountId(currentAccountId).then(hashedId => {
        pushFeedAnalyticsEvent({
          event: "feed_to_game_page",
          account_id: hashedId,
          hubsid: hubSid
        });
      });
    };
    t2 = [enableFull3d, roomData.hub_sid, currentAccountId];
    $[0] = currentAccountId;
    $[1] = enableFull3d;
    $[2] = roomData.hub_sid;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== enableFull3d || $[6] !== iosSwipeFullscreen.dismiss || $[7] !== iosSwipeFullscreen.showSwipeLayer || $[8] !== isMobile || $[9] !== isTrailer || $[10] !== isVideoWorld || $[11] !== roomData.image || $[12] !== setScrollLock || $[13] !== shouldShowMobileTip || $[14] !== shouldShowTrailerThumbnails || $[15] !== video) {
    t3 = <CrossPlatformGatingProvider><CheckEnterRoomScreen>{isTrailer || shouldShowMobileTip ? <TrailerViewMediaProvider video={video}><TrailerCarousel />{shouldShowTrailerThumbnails && <div className="relative w-full overflow-hidden max-lg:hidden"><TrailerThumbnails /></div>}</TrailerViewMediaProvider> : <div className="relative h-full w-full"><VastAdOverlay preload={isVideoWorld} loadingPlaceholder={!isMobile && !enableFull3d && roomData.image ? <OptimizedImage src={roomData.image} alt="" fill={true} loading="eager" fetchPriority="high" sizes="(max-width: 1023px) 100vw, (max-width: 1439px) 66vw, (max-width: 1599px) 60vw, 1296px" className="pointer-events-none object-cover" /> : undefined}><IframeContent setScrollLock={setScrollLock} />{iosSwipeFullscreen.showSwipeLayer && <IosSwipeFullscreenLayer onDismiss={iosSwipeFullscreen.dismiss} />}</VastAdOverlay></div>}</CheckEnterRoomScreen></CrossPlatformGatingProvider>;
    $[5] = enableFull3d;
    $[6] = iosSwipeFullscreen.dismiss;
    $[7] = iosSwipeFullscreen.showSwipeLayer;
    $[8] = isMobile;
    $[9] = isTrailer;
    $[10] = isVideoWorld;
    $[11] = roomData.image;
    $[12] = setScrollLock;
    $[13] = shouldShowMobileTip;
    $[14] = shouldShowTrailerThumbnails;
    $[15] = video;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const worldContent = t3;
  const t4 = !enableFull3d;
  const t5 = !isTrailer && !isMobile;
  let t6;
  if ($[17] !== is3DCommerceTransparentBg || $[18] !== t4 || $[19] !== t5) {
    t6 = cn("relative w-full overflow-hidden", {
      "md:rounded-[15px] landscape:rounded-none": t4,
      "bg-[rgba(0,0,0,0.6)]": t5,
      "bg-transparent": is3DCommerceTransparentBg
    });
    $[17] = is3DCommerceTransparentBg;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== className || $[22] !== enableFull3d || $[23] !== iosSwipeFullscreen.absorbSwipes || $[24] !== iosSwipeFullscreen.active || $[25] !== isMobilePortrait || $[26] !== worldContent) {
    t7 = enableFull3d ? <div className={cn("flex w-full flex-col", iosSwipeFullscreen.active ? "fixed inset-x-0 top-0 h-[var(--full3d-viewport-height,100dvh)]" : "h-dvh", iosSwipeFullscreen.absorbSwipes && "overflow-x-hidden overflow-y-auto overscroll-none [scrollbar-width:none]", className)}><div className="relative min-h-0 flex-1">{worldContent}</div>{isMobilePortrait && <Full3dMobileStickyBannerAd />}{iosSwipeFullscreen.absorbSwipes && <div aria-hidden={true} className="pointer-events-none absolute top-0 left-0 h-[calc(100%+1px)] w-px" />}</div> : <div className={className}>{worldContent}</div>;
    $[21] = className;
    $[22] = enableFull3d;
    $[23] = iosSwipeFullscreen.absorbSwipes;
    $[24] = iosSwipeFullscreen.active;
    $[25] = isMobilePortrait;
    $[26] = worldContent;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== iosSwipeFullscreen.active) {
    t8 = iosSwipeFullscreen.active && <div aria-hidden={true} className="h-[300lvh]" />;
    $[28] = iosSwipeFullscreen.active;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t9 = <div ref={worldRootRef} data-component="world-player" className={t6}>{t7}{t8}</div>;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
}
export default World;